import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import '@tylertech/forge/button';
import { ColorPickerField } from '../forms';
import type { User } from '../../store/types';
import type { TagValueType, TagValueValidation } from '../../graphql/types';
import './TagCategoryForm.css';

export interface TagCategoryFormData {
  name: string;
  description?: string;
  scope: 'global' | 'team';
  scopeOwnerId?: string;
  color?: string;
  allowMultiple: boolean;
  /** Custom-field type (issue #184); `label-only` keeps the historical behavior. */
  valueType: TagValueType;
  /** Optional typed-value constraints — flattened for the form, nested on submit. */
  validationMin?: number;
  validationMax?: number;
  validationRegex?: string;
}

const VALUE_TYPE_OPTIONS: { value: TagValueType; label: string }[] = [
  { value: 'label-only', label: 'Label only (no value)' },
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'url', label: 'URL' },
];

/**
 * Build the nested `validation` object from the form's flattened fields.
 * Returns undefined for label-only categories or when no constraint is set,
 * so the request omits the field entirely.
 */
export function buildValidationFromForm(data: TagCategoryFormData): TagValueValidation | undefined {
  if (data.valueType === 'label-only') return undefined;
  const validation: TagValueValidation = {};
  if (typeof data.validationMin === 'number' && !Number.isNaN(data.validationMin)) {
    validation.min = data.validationMin;
  }
  if (typeof data.validationMax === 'number' && !Number.isNaN(data.validationMax)) {
    validation.max = data.validationMax;
  }
  if (data.validationRegex && data.validationRegex.trim() !== '') {
    validation.regex = data.validationRegex.trim();
  }
  return Object.keys(validation).length > 0 ? validation : undefined;
}

export interface TagCategoryFormProps {
  defaultValues?: Partial<TagCategoryFormData>;
  onSubmit: (data: TagCategoryFormData) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  mode: 'create' | 'edit';
  currentUser: User | null;
  userTeams?: { id: string; name: string }[];
}

export function TagCategoryForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
  currentUser,
  userTeams = [],
}: TagCategoryFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<TagCategoryFormData>({
    defaultValues: {
      allowMultiple: true,
      scope: 'team',
      valueType: 'label-only',
      ...defaultValues,
    },
  });

  useEffect(() => {
    if (defaultValues) {
      reset({ allowMultiple: true, scope: 'team', valueType: 'label-only', ...defaultValues });
    }
  }, [defaultValues, reset]);

  const scope = watch('scope');
  const valueType = watch('valueType');
  const isTyped = valueType !== 'label-only';
  const supportsRegex = valueType === 'string' || valueType === 'url';
  const supportsRange = valueType === 'number' || valueType === 'string' || valueType === 'url';
  const isAdmin = currentUser?.roles.some((r) =>
    ['Administrator', 'administrator', 'admin'].includes(r)
  );

  const ForgeButton = 'forge-button' as React.ElementType;

  return (
    <form className="tag-category-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="tag-category-form__field">
        <label htmlFor="category-name">Category Name</label>
        <input
          id="category-name"
          type="text"
          aria-label="Category Name"
          {...register('name', { required: 'Name is required' })}
        />
        {errors.name && (
          <span className="tag-category-form__error" role="alert">
            {errors.name.message}
          </span>
        )}
      </div>

      <div className="tag-category-form__field">
        <label htmlFor="category-scope">Scope</label>
        <select
          id="category-scope"
          aria-label="Scope"
          {...register('scope', { required: 'Scope is required' })}
          disabled={mode === 'edit'}
        >
          {isAdmin && <option value="global">Global (all users)</option>}
          <option value="team">Team-scoped</option>
        </select>
        {errors.scope && (
          <span className="tag-category-form__error" role="alert">
            {errors.scope.message}
          </span>
        )}
      </div>

      {scope === 'team' && (
        <div className="tag-category-form__field">
          <label htmlFor="category-scope-owner">Owning Team</label>
          <select
            id="category-scope-owner"
            aria-label="Owning Team"
            {...register('scopeOwnerId', {
              required: scope === 'team' ? 'Owning team is required for team scope' : false,
            })}
          >
            <option value="">Select a team</option>
            {userTeams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
          {errors.scopeOwnerId && (
            <span className="tag-category-form__error" role="alert">
              {errors.scopeOwnerId.message}
            </span>
          )}
        </div>
      )}

      <div className="tag-category-form__field">
        <label htmlFor="category-description">Description</label>
        <textarea
          id="category-description"
          aria-label="Description"
          rows={3}
          {...register('description')}
        />
      </div>

      <div className="tag-category-form__field">
        <label htmlFor="category-color">Color</label>
        <ColorPickerField
          id="category-color"
          ariaLabel="Color"
          value={watch('color')}
          onChange={(hex) => setValue('color', hex, { shouldValidate: true })}
        />
      </div>

      <div className="tag-category-form__field tag-category-form__field--checkbox">
        <input
          id="category-allow-multiple"
          type="checkbox"
          aria-label="Allow multiple tags from this category per entity"
          {...register('allowMultiple')}
        />
        <label htmlFor="category-allow-multiple">
          Allow multiple tags from this category per entity
        </label>
      </div>

      <div className="tag-category-form__field">
        <label htmlFor="category-value-type">Field Type</label>
        <select
          id="category-value-type"
          aria-label="Field Type"
          disabled={mode === 'edit'}
          {...register('valueType', { required: 'Field type is required' })}
        >
          {VALUE_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {errors.valueType && (
          <span className="tag-category-form__error" role="alert">
            {errors.valueType.message}
          </span>
        )}
      </div>

      {isTyped && supportsRange && (
        <div className="tag-category-form__field tag-category-form__field--range">
          <div>
            <label htmlFor="category-validation-min">Min</label>
            <input
              id="category-validation-min"
              type="number"
              step="any"
              aria-label="Minimum"
              {...register('validationMin', { valueAsNumber: true })}
            />
          </div>
          <div>
            <label htmlFor="category-validation-max">Max</label>
            <input
              id="category-validation-max"
              type="number"
              step="any"
              aria-label="Maximum"
              {...register('validationMax', { valueAsNumber: true })}
            />
          </div>
        </div>
      )}

      {isTyped && supportsRegex && (
        <div className="tag-category-form__field">
          <label htmlFor="category-validation-regex">Pattern (regex)</label>
          <input
            id="category-validation-regex"
            type="text"
            aria-label="Validation pattern"
            {...register('validationRegex')}
          />
        </div>
      )}

      <div className="tag-category-form__actions">
        <ForgeButton type="button" variant="outlined" onClick={onCancel}>
          Cancel
        </ForgeButton>
        <ForgeButton type="submit" variant="raised" disabled={isSubmitting}>
          {mode === 'create' ? 'Create Category' : 'Save'}
        </ForgeButton>
      </div>
    </form>
  );
}
